/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useCallback, useContext, useState, useMemo } from 'react';
import { contexts, hooks, unstable, utils } from '@broadleaf/admin-components';
import { get, isNil, isString, upperCase } from 'lodash';

const { ToastContainerContext } = contexts;
const { usePollEffect, useEventCallback } = hooks;
const { DownloadLink, ModalFormAction, SVG, MetadataUtils, RequestUtils } =
  unstable;
const { log, RequestUtils: Request } = utils;
const { getAttribute, getEndpointByType } = MetadataUtils;
const { compile, getErrorMessage } = RequestUtils;
const logger = log.getLogger(
  'form.components.TreeView.components.action-components.global.ImportTranslations'
);
const defaultClassName =
  'tw-flex tw-items-center tw-justify-start tw-w-full tw-py-4 tw-px-3 tw-text-gray-700 tw-border-t tw-border-gray-300 tw-font-medium tw-text-lg tw-whitespace-no-wrap tw-bg-gray-100 hover:tw-bg-gray-200 hover:tw-text-gray-900 focus:tw-bg-gray-200 focus:tw-text-gray-900 focus:tw-outline-none active:tw-bg-gray-300 md:tw-py-2 md:tw-text-sm';

const ImportTranslations = props => {
  const { actionDefinition, readOnly: disabled = false } = props;
  const polling = usePollImportById(props);
  const startImport = useStartImport({ ...props, polling });
  const renderAction = useCallback(
    ({ toggleOpen }) => (
      <button
        className={defaultClassName}
        disabled={disabled}
        onClick={toggleOpen}
      >
        <SVG
          className="tw-mr-2 tw-inline tw-h-4 tw-w-4 tw-fill-current"
          name="upload"
        />{' '}
        {actionDefinition.label}
      </button>
    ),
    [actionDefinition, disabled]
  );

  return (
    <ModalFormAction
      components={actionDefinition.components}
      disabled={disabled}
      onSubmit={startImport}
      renderAction={renderAction}
      submitLabel={getAttribute(actionDefinition, 'submitLabel', 'Submit')}
      title={actionDefinition.label}
      tooltipPlacement={'top'}
    />
  );
};

/**
 * Starts polling of the created import to check the status.
 * If the import is completed with errors, a message appears with a link to download the import file with errors.
 */
function usePollImportById(props) {
  const [active, setActive] = useState(false);
  const [importId, setImportId] = useState(null);

  const polling = {
    active,
    startPoll: importId => {
      setImportId(importId);
      setActive(true);
    },
    stopPoll: () => setActive(false)
  };

  const readResult = useReadImportById({ ...props, polling });

  const options = useMemo(
    () => ({
      strategy: {
        ...usePollEffect.defaultStrategy,
        interval: 2000
      },
      importId
    }),
    [importId]
  );

  usePollEffect(readResult, active, options);

  return polling;
}

/**
 * Reads the import by its id. It requires the endpoint with "READ_IMPORT" type.
 */
function useReadImportById(props) {
  const {
    actionDefinition,
    getContextParams,
    handleRead,
    polling,
    refreshChangeSummaries
  } = props;
  const toast = useContext(ToastContainerContext);

  const config = useMemo(() => {
    const importConfig = getEndpointByType(actionDefinition, 'READ_IMPORT');

    const downloadImportErrorsUri = getAttribute(
      actionDefinition,
      'downloadImportErrorsUri'
    );

    const downloadImportErrorsScope = getAttribute(
      actionDefinition,
      'downloadImportErrorsScope'
    );

    return {
      importConfig,
      importContextParams: getContextParams({}),
      downloadImportErrorsUri,
      downloadImportErrorsScope
    };
  }, [actionDefinition, getContextParams]);

  return useEventCallback(
    ({ importId }) => {
      const context = { ...config.importContextParams, importId };
      return Request.request(config.importConfig, context)
        .then(response => response.data)
        .then(lastImport => {
          const status = get(lastImport, 'status');
          if (
            isNil(lastImport) ||
            status === 'COMPLETED' ||
            status === 'FAILED'
          ) {
            polling.stopPoll();
          }

          if (status === 'COMPLETED') {
            if (get(lastImport, 'errorCount') > 0) {
              const downloadImportErrorsUri = compile(
                config.downloadImportErrorsUri
              )(context);

              toast.addToast(
                <>
                  <div>
                    {getAttribute(
                      actionDefinition,
                      'downloadFailedMessage',
                      'The import completed with errors'
                    )}
                  </div>
                  <DownloadLink
                    href={downloadImportErrorsUri}
                    scope={config.downloadImportErrorsScope}
                    label={getAttribute(
                      actionDefinition,
                      'downloadFailedLabel',
                      'Download and Retry'
                    )}
                  />
                  <div>
                    {getAttribute(
                      actionDefinition,
                      'downloadFailedHelpText',
                      'Downloads a file with additional error information'
                    )}
                  </div>
                </>,
                { type: 'error' }
              );
            } else {
              toast.addToast(
                getAttribute(
                  actionDefinition,
                  'succeededMessage',
                  'The import completed successfully!'
                ),
                {
                  autoClose: 5000,
                  closeOnClick: true,
                  type: 'success'
                }
              );
            }

            refreshChangeSummaries && refreshChangeSummaries();
            handleRead && handleRead();
          } else if (status === 'ERROR') {
            const error = get(
              lastImport,
              'errorMessage',
              getAttribute(
                actionDefinition,
                'errorMessage',
                'The import failed'
              )
            );
            toast.addToast(error, {
              type: 'error'
            });
          }
        })
        .catch(error => {
          polling.stopPoll();
          if (!isString(error)) {
            error = getErrorMessage(error);
          }

          toast.addToast(error, {
            autoClose: 5000,
            closeOnClick: true,
            type: 'error'
          });

          logger.debug('An error occurred while reading the imports: ', error);
          return Promise.reject(error);
        });
    },
    [config, refreshChangeSummaries]
  );
}

/**
 * This hook is used to start a new import. It requires the endpoint with "START_IMPORT" type.
 * If the import started successfully the polling of this import will be started to check the status.
 * @see usePollImportById
 */
function useStartImport(props) {
  const {
    actionDefinition,
    getContextParams,
    isCatalogDiscriminated,
    polling
  } = props;

  const toast = useContext(ToastContainerContext);
  const contextParams = getContextParams({});

  const config = useMemo(() => {
    const importType = getAttribute(actionDefinition, 'importType');
    const importEndpoint = getEndpointByType(actionDefinition, 'START_IMPORT');
    const changeContainer = get(contextParams, 'changeContainer');
    // just going to assume this is sandbox discriminated for now since that
    // info is not available here and Category and MenuItem are sandboxable
    const importingSandboxId = get(
      contextParams,
      'tracking.sandbox.currentSandboxId'
    );
    const importingCatalogId = isCatalogDiscriminated
      ? get(contextParams, 'tracking.catalog.currentCatalogId')
      : undefined;

    return {
      importConfig: {
        ...importEndpoint,
        headers: {
          ...importEndpoint.headers,
          'Content-Type': 'multipart/form-data'
        }
      },
      importContextParams: contextParams,
      request: {
        changeContainer,
        importingSandboxId,
        importingCatalogId,
        productionImport: false,
        type: importType
      }
    };
  }, [actionDefinition, contextParams, isCatalogDiscriminated]);

  return useEventCallback(
    values => {
      const { file, ...contextProps } = values;

      const filename = get(file, 'name');
      const fileType = get(contextProps, 'fileType', getFileType(filename));

      const mergedConfig = {
        ...config.importConfig,
        data: {
          data: file,
          request: {
            ...config.request,
            fileType,
            ...contextProps
          }
        }
      };

      const mergedContext = {
        ...config.importContextParams,
        ...contextProps
      };

      return Request.request(mergedConfig, mergedContext)
        .then(response => response.data)
        .then(createdImport => {
          toast.addToast(
            getAttribute(
              actionDefinition,
              'startedMessage',
              'Import started successfull!'
            ),
            {
              autoClose: 5000,
              closeOnClick: true,
              type: 'info'
            }
          );

          polling.startPoll(createdImport.id);
        })
        .catch(error => {
          logger.debug(
            'An error occurred while creating a new Import: ',
            error
          );
          return Promise.reject(error);
        });
    },
    [config]
  );
}

function getFileType(filename) {
  const fileType = filename.substring(
    filename.lastIndexOf('.') + 1,
    filename.length
  );

  return isNil(fileType) ? 'CSV' : upperCase(fileType);
}

export default ImportTranslations;
export { ImportTranslations };
